import { ArrowDown, ArrowUp } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import type { AttemptQuestion } from '@/types/api';
import { cn } from '@/lib/utils';
import { Input, Select, Textarea } from '@/components/ui';
import { moveItem, pairedWith, setPair, type AnswerDraft } from '@/features/quiz/answers';

interface QuestionInputProps {
  question: AttemptQuestion;
  draft: AnswerDraft;
  onChange: (draft: AnswerDraft) => void;
}

/**
 * Saisie d'une reponse, adaptee au type de question.
 * Answer input, matching the question type.
 */
export function QuestionInput({ question, draft, onChange }: QuestionInputProps) {
  const { t, language, pick } = useLanguage();

  if (question.type === 'FILL_BLANK' || question.type === 'FREE_TEXT') {
    const long = question.type === 'FREE_TEXT';
    return (
      <div className="space-y-2">
        {long ? (
          <Textarea
            rows={8}
            value={draft.textAnswer}
            onChange={(event) => onChange({ ...draft, textAnswer: event.target.value })}
            aria-label={pick(question, 'text')}
            placeholder={t('quiz.freeTextPlaceholder')}
          />
        ) : (
          <Input
            value={draft.textAnswer}
            onChange={(event) => onChange({ ...draft, textAnswer: event.target.value })}
            aria-label={pick(question, 'text')}
            placeholder={t('quiz.fillBlankPlaceholder')}
          />
        )}
        <p className="text-xs text-ink-muted">{long ? t('quiz.freeTextHint') : t('quiz.fillBlankHint')}</p>
      </div>
    );
  }

  if (question.type === 'ORDERING') {
    return (
      <ol className="space-y-2">
        {draft.orderedChoiceIds.map((choiceId, position) => {
          const choice = question.choices.find((item) => item.id === choiceId);
          if (!choice) return null;
          return (
            <li key={choiceId} className="flex items-center gap-3 rounded-xl border border-border p-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-xs font-semibold text-primary">
                {position + 1}
              </span>
              <span className="min-w-0 flex-1 text-sm text-ink">{pick(choice, 'text')}</span>
              <span className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  disabled={position === 0}
                  aria-label={t('quiz.moveUp')}
                  onClick={() =>
                    onChange({ ...draft, orderedChoiceIds: moveItem(draft.orderedChoiceIds, position, position - 1) })
                  }
                  className="rounded-lg p-1.5 text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink disabled:opacity-30"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={position === draft.orderedChoiceIds.length - 1}
                  aria-label={t('quiz.moveDown')}
                  onClick={() =>
                    onChange({ ...draft, orderedChoiceIds: moveItem(draft.orderedChoiceIds, position, position + 1) })
                  }
                  className="rounded-lg p-1.5 text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink disabled:opacity-30"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
              </span>
            </li>
          );
        })}
      </ol>
    );
  }

  if (question.type === 'MATCHING') {
    const targets = question.matches ?? [];
    return (
      <ul className="space-y-2">
        {question.choices.map((choice) => {
          const value = pairedWith(draft.orderedChoiceIds, choice.id);
          return (
            <li key={choice.id} className="grid items-center gap-2 rounded-xl border border-border p-3 sm:grid-cols-2">
              <span className="text-sm font-medium text-ink">{pick(choice, 'text')}</span>
              <Select
                value={value}
                aria-label={`${pick(choice, 'text')} — ${t('quiz.matchWith')}`}
                onChange={(event) =>
                  onChange({ ...draft, orderedChoiceIds: setPair(draft.orderedChoiceIds, choice.id, event.target.value) })
                }
              >
                <option value="">{t('quiz.matchWith')}…</option>
                {targets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {language === 'en' ? target.textEn : target.textFr}
                  </option>
                ))}
              </Select>
            </li>
          );
        })}
      </ul>
    );
  }

  // Questions a cocher : un seul choix, ou plusieurs.
  const multiple = question.type === 'MULTIPLE_CHOICE';

  const toggle = (choiceId: string) => {
    if (!multiple) {
      onChange({ ...draft, selectedChoiceIds: [choiceId] });
      return;
    }
    onChange({
      ...draft,
      selectedChoiceIds: draft.selectedChoiceIds.includes(choiceId)
        ? draft.selectedChoiceIds.filter((id) => id !== choiceId)
        : [...draft.selectedChoiceIds, choiceId],
    });
  };

  return (
    <fieldset className="space-y-2">
      <legend className="sr-only">{pick(question, 'text')}</legend>
      {question.choices.map((choice) => {
        const isSelected = draft.selectedChoiceIds.includes(choice.id);
        return (
          <label
            key={choice.id}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-all',
              isSelected
                ? 'border-primary bg-primary-soft ring-1 ring-primary'
                : 'border-border hover:border-primary/40 hover:bg-surface-muted',
            )}
          >
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={question.id}
              checked={isSelected}
              onChange={() => toggle(choice.id)}
              className="h-4 w-4 shrink-0 accent-[hsl(var(--primary))]"
            />
            <span className="text-sm text-ink">{pick(choice, 'text')}</span>
          </label>
        );
      })}
    </fieldset>
  );
}
